import { useEffect, useRef } from "react";

import { MissionProgress } from "./MissionProgress";
import { MissionPauseButton } from "./MissionPauseButton";
import type { MissionShellProps } from "./types";

/**
 * MissionShell — the reusable Focus Flow surface.
 *
 * Responsibilities (locked):
 * - Enforce the Three Questions contract via required props.
 * - Own the primary CTA (exactly one, visually dominant).
 * - Own the pause/close control (persist memory, do not cancel).
 * - Full-screen on mobile with body-scroll lock and logical focus trap.
 * - Side-rail on desktop; leaving the route preserves state (the
 *   mutation queue handles memory persistence).
 */
export function MissionShell({
  mission,
  missionTitle,
  stageLabel,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  onPause,
  status,
  primaryActionState = "idle",
  children,
  variant = "auto",
  contentSize = "default",
  testIdRoot = "mission-shell",
}: MissionShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Mobile scroll lock — only when the shell is rendered as an overlay
  // (auto + narrow viewport, or the caller explicitly asks for it).
  useEffect(() => {
    if (variant === "side-rail") return;
    const previous = document.body.style.overflow;
    // Only apply when the viewport is narrow enough that the overlay
    // actually covers the page.
    const shouldLock = window.matchMedia("(max-width: 767px)").matches
      || variant === "overlay";
    if (shouldLock) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = previous;
    };
  }, [variant]);

  // Move initial focus to the primary CTA — the founder should be one
  // Enter press away from the next decision.
  useEffect(() => {
    primaryRef.current?.focus();
  }, [mission.id, mission.stage]);

  // Logical focus trap for the overlay layout. Tab / Shift+Tab cycles
  // between focusables inside the shell.
  useEffect(() => {
    if (variant === "side-rail") return;
    const node = rootRef.current;
    if (!node) return;
    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = node.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener("keydown", trap);
    return () => node.removeEventListener("keydown", trap);
  }, [variant, mission.id]);

  const isPending = primaryActionState === "pending";
  const isDisabled = primaryActionState === "disabled";

  // Locked visual language: soft charcoal card, cyan-royal-violet
  // gradient CTA (Champagne Gold is reserved for headline actions per
  // Patch 36.2 — mission CTAs speak the intelligence gradient because
  // Akinex is guiding the mission).
  return (
    <section
      ref={rootRef}
      data-testid={testIdRoot}
      data-mission-id={mission.id}
      data-mission-type={mission.mission_type}
      data-mission-status={mission.status}
      data-mission-stage={mission.stage}
      data-mission-version={mission.version}
      role="region"
      aria-live="polite"
      aria-label={missionTitle}
      className={
        "focus-flow-shell fixed inset-0 z-40 flex flex-col bg-[color:var(--brand-obsidian,#0B0D12)] "
        + "md:relative md:inset-auto md:z-auto md:mx-auto md:my-8 md:rounded-2xl md:border md:border-white/10 md:bg-[color:var(--brand-charcoal,#12151C)] md:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.8)] "
        + (contentSize === "wide" ? "md:max-w-4xl" : "md:max-w-2xl")
      }
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header: three-question line + pause */}
      <header
        className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4 md:px-6"
        data-testid={`${testIdRoot}-header`}
      >
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80"
            data-testid={`${testIdRoot}-stage-label`}
          >
            {stageLabel}
          </div>
          <h1
            className="mt-1 text-lg md:text-xl font-semibold text-white leading-tight truncate"
            data-testid={`${testIdRoot}-title`}
          >
            {missionTitle}
          </h1>
        </div>
        <MissionPauseButton onPause={onPause} testIdRoot={testIdRoot} />
      </header>

      {/* Progress strip */}
      <div className="px-5 md:px-6 pt-3 pb-2 border-b border-white/5">
        <MissionProgress progress={mission.progress} testIdRoot={testIdRoot} />
      </div>

      {/* Body — the current decision. Scrolls independently on mobile. */}
      <div
        className="flex-1 overflow-y-auto px-5 md:px-6 py-5"
        data-testid={`${testIdRoot}-body`}
      >
        {children}
      </div>

      {/* Footer — status + secondary + primary CTA. Sticky on mobile
          so the founder never has to scroll to advance. */}
      <footer
        className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-white/10 bg-[color:var(--brand-charcoal,#12151C)] px-5 md:px-6 py-4"
        data-testid={`${testIdRoot}-footer`}
      >
        <div
          className="text-xs text-white/50 truncate min-h-[1rem]"
          data-testid={`${testIdRoot}-status`}
          aria-live="polite"
        >
          {status || ""}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {secondaryActionLabel && onSecondaryAction && (
            <button
              type="button"
              onClick={onSecondaryAction}
              disabled={isPending}
              className="rounded-full border border-white/15 bg-transparent px-4 py-2 text-sm font-medium text-white/80 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-50 transition"
              data-testid={`${testIdRoot}-secondary`}
            >
              {secondaryActionLabel}
            </button>
          )}
          <button
            ref={primaryRef}
            type="button"
            onClick={onPrimaryAction}
            disabled={isPending || isDisabled}
            className="rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-5 py-2 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60 disabled:cursor-not-allowed transition"
            data-testid={`${testIdRoot}-primary`}
            data-action-state={primaryActionState}
            aria-label={primaryActionLabel}
          >
            {isPending ? "Working…" : primaryActionLabel}
          </button>
        </div>
      </footer>
    </section>
  );
}
